import React, { useState, useEffect } from 'react';
import {
  Search,
  Bell,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  Users,
  Calculator,
  ShieldAlert,
  Download,
  Calendar,
  Layers,
  Activity,
  UserCheck,
  Zap,
  DollarSign,
  Database,
  Globe,
  Sliders,
  HelpCircle,
  LogOut,
  ChevronRight,
  RefreshCw,
  ExternalLink,
  X,
  Check,
  Filter,
  Info,
  Clock,
  Radio,
  Plus
} from 'lucide-react';
import { AppView } from '../../types';
import {
  getControlCenterState,
  subscribeControlCenter,
  setTimeFilter,
  resolveActionRequired,
  assignActionRequired,
  ActionRequiredItem,
  ChangeEvent,
  ControlCenterState
} from '../../services/admin/controlCenterStore';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface ControlCenterOverviewProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export const ControlCenterOverview: React.FC<ControlCenterOverviewProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [state, setState] = useState<ControlCenterState>(getControlCenterState());
  const [selectedDomain, setSelectedDomain] = useState<string>('overview');
  const [metricTab, setMetricTab] = useState<'calculations' | 'activeUsers' | 'apiRequests' | 'latencyMs'>('calculations');
  const [severityFilter, setSeverityFilter] = useState<'ALL' | 'CRITICAL' | 'WARNING' | 'INFO'>('ALL');
  const [activeException, setActiveException] = useState<ActionRequiredItem | null>(null);
  const [showNotificationsPopover, setShowNotificationsPopover] = useState(false);
  const [showProfilePopover, setShowProfilePopover] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribeControlCenter(() => {
      setState(getControlCenterState());
    });
    return () => unsub();
  }, []);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const filteredActions = state.actionsRequired.filter((act) => {
    if (act.status === 'RESOLVED') return false;
    if (severityFilter === 'ALL') return true;
    return act.severity === severityFilter;
  });

  const getKPIIcon = (iconName: string) => {
    switch (iconName) {
      case 'users':
        return <Users className="w-5 h-5 text-blue-600" />;
      case 'calculator':
        return <Calculator className="w-5 h-5 text-amber-600" />;
      case 'trending-up':
        return <TrendingUp className="w-5 h-5 text-blue-600" />;
      case 'check-circle-2':
        return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
      default:
        return <Activity className="w-5 h-5 text-blue-600" />;
    }
  };

  const getDotColor = (dotColor: string) => {
    switch (dotColor) {
      case 'red':
        return 'bg-red-500 shadow-xs shadow-red-200';
      case 'orange':
        return 'bg-amber-500 shadow-xs shadow-amber-200';
      case 'blue':
        return 'bg-blue-500';
      default:
        return 'bg-slate-400';
    }
  };

  const handleExportData = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(state, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `yamoopay-control-center-overview-${Date.now()}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    triggerToast('Control Center report exported successfully.');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (7 Canonical Domains)                           */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#2563EB] flex items-center justify-center text-white shadow-xs font-black text-lg">
            Y
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              YamooPay
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              CONTROL CENTER
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-6">
          {/* Section: CORE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              CORE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('overview');
                  onNavigateToView('admin-super-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedDomain === 'overview'
                    ? 'bg-[#2563EB] text-white shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('customers');
                  onNavigateToView('admin-organizations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'customers'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Customers & Support</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('product');
                  onNavigateToView('cpo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'product'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Product & Growth</span>
              </button>
            </nav>
          </div>

          {/* Section: PERFORMANCE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              PERFORMANCE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('revenue');
                  onNavigateToView('cfo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'revenue'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>Revenue & Finance</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('data');
                  onNavigateToView('cdo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'data'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Data & AI</span>
              </button>
            </nav>
          </div>

          {/* Section: SYSTEM */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              SYSTEM
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('operations');
                  onNavigateToView('admin-system-health');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'operations'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Operations & Risk</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('configuration');
                  onNavigateToView('admin-roles-permissions');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'configuration'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Sliders className="w-4 h-4" />
                <span>Configuration</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-[#F1F5F9] space-y-1">
          <button
            onClick={() => onNavigateToView('help-center')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] transition-colors cursor-pointer"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Support Docs</span>
          </button>
          <button
            onClick={() => {
              triggerToast('Signed out of SuperAdmin session');
              onNavigateToView('homepage');
            }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-[#64748B] hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-6 flex items-center justify-between sticky top-0 z-20 shadow-2xs">
          {/* Global Search Button */}
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <button
              onClick={onOpenSearch}
              className="w-full flex items-center justify-between px-3.5 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#64748B] hover:border-[#CBD5E1] transition-all cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-[#94A3B8]" />
                <span>Search across operations...</span>
              </div>
              <kbd className="px-1.5 py-0.5 bg-white border border-[#CBD5E1] rounded text-[10px] font-mono font-bold text-[#64748B]">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Status Badges & Admin Actions */}
          <div className="flex items-center gap-3">
            {/* Environment Badge */}
            <div className="px-2.5 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-xs font-extrabold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>PRODUCTION</span>
            </div>

            {/* System Health Badge */}
            <button
              onClick={() => onNavigateToView('admin-system-health')}
              className="px-3 py-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-xs font-semibold text-[#334155] hover:bg-[#F1F5F9] flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>All Systems Operational</span>
            </button>

            {/* Notifications Icon Button */}
            <div className="relative">
              <button
                onClick={() => setShowNotificationsPopover(!showNotificationsPopover)}
                className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors relative cursor-pointer"
              >
                <Bell className="w-5 h-5" />
                {state.unreadNotificationsCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
                )}
              </button>

              {/* Notifications Popover */}
              {showNotificationsPopover && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-[#E2E8F0] rounded-2xl shadow-xl p-4 z-50 space-y-3 animate-in fade-in zoom-in-95 duration-150">
                  <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-2">
                    <h4 className="text-xs font-bold text-[#0F172A]">System Notifications</h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                      {state.unreadNotificationsCount} New
                    </span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="p-2 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                      <span className="font-bold text-red-600 block">Security Alert</span>
                      <p className="text-[#64748B] text-[11px]">Credential stuffing blocked on auth endpoints.</p>
                    </div>
                    <div className="p-2 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                      <span className="font-bold text-amber-600 block">SLA Warning</span>
                      <p className="text-[#64748B] text-[11px]">2 Enterprise support tickets nearing SLA expiry.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Admin Profile Avatar */}
            <div className="relative">
              <button
                onClick={() => setShowProfilePopover(!showProfilePopover)}
                className="w-9 h-9 rounded-full bg-[#E2E8F0] border border-[#CBD5E1] overflow-hidden cursor-pointer flex items-center justify-center font-bold text-xs text-[#334155] hover:ring-2 hover:ring-[#2563EB]"
              >
                <UserCheck className="w-5 h-5 text-[#2563EB]" />
              </button>

              {/* Profile Popover */}
              {showProfilePopover && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#E2E8F0] rounded-2xl shadow-xl p-4 z-50 space-y-3 animate-in fade-in zoom-in-95 duration-150">
                  <div className="border-b border-[#F1F5F9] pb-2">
                    <p className="text-xs font-bold text-[#0F172A]">{state.adminProfile.name}</p>
                    <p className="text-[11px] font-mono text-[#64748B]">{state.adminProfile.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-[#2563EB] text-[10px] font-bold rounded">
                      {state.adminProfile.role}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center justify-between text-[#64748B]">
                      <span>MFA Status</span>
                      <span className="text-emerald-600 font-bold">Enforced</span>
                    </div>
                    <div className="flex items-center justify-between text-[#64748B]">
                      <span>Session Time</span>
                      <span className="font-mono text-[11px]">42m active</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Action Button */}
            <button
              onClick={() => onNavigateToView('tools')}
              className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>New Calculation</span>
            </button>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-8 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* Overview Header & Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">
                Control Center Overview
              </h1>
              <p className="text-xs font-medium text-[#64748B] mt-1">
                Real-time pulse of global operations and platform health.
              </p>
            </div>

            {/* Time Controls & Export */}
            <div className="flex items-center gap-3">
              <div className="bg-white border border-[#E2E8F0] p-1 rounded-xl shadow-2xs flex items-center gap-1">
                {(['Today', '7D', '30D', 'Custom'] as const).map((tf) => (
                  <button
                    key={tf}
                    onClick={() => setTimeFilter(tf)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      state.timeFilter === tf
                        ? 'bg-[#F1F5F9] text-[#0F172A] shadow-xs'
                        : 'text-[#64748B] hover:text-[#0F172A]'
                    }`}
                  >
                    {tf === 'Custom' ? '📅 Custom' : tf}
                  </button>
                ))}
              </div>

              <button
                onClick={handleExportData}
                className="px-3.5 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#334155] font-bold text-xs rounded-xl shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Download className="w-4 h-4 text-[#64748B]" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 3. KPI ROW (4 Core Real Metrics - No fake dollar volumes)       */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {state.kpis.map((kpi) => (
              <div
                key={kpi.id}
                className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#64748B] tracking-wide">
                    {kpi.label}
                  </span>
                  <div className={`p-2 rounded-xl ${kpi.badgeBg}`}>
                    {getKPIIcon(kpi.icon)}
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                    {kpi.value}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#64748B]">
                    {kpi.changeDirection === 'up' ? (
                      <span className="text-emerald-600 font-bold flex items-center gap-0.5">
                        ↑ {kpi.change}
                      </span>
                    ) : kpi.changeDirection === 'down' ? (
                      <span className="text-red-600 font-bold flex items-center gap-0.5">
                        ↓ {kpi.change}
                      </span>
                    ) : (
                      <span className="text-[#64748B] font-bold">
                        — {kpi.change}
                      </span>
                    )}
                    <span>{kpi.timeframe}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* =============================================================== */}
          {/* 4. ACTION REQUIRED (Central Central Exception Container)        */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[#F1F5F9] pb-4">
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h2 className="text-base font-extrabold text-[#0F172A]">
                  Action Required ({filteredActions.length})
                </h2>
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-2">
                {(['ALL', 'CRITICAL', 'WARNING', 'INFO'] as const).map((sev) => (
                  <button
                    key={sev}
                    onClick={() => setSeverityFilter(sev)}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      severityFilter === sev
                        ? 'bg-[#0F172A] text-white'
                        : 'bg-[#F1F5F9] text-[#64748B] hover:text-[#0F172A]'
                    }`}
                  >
                    {sev}
                  </button>
                ))}
                <button
                  onClick={() => onNavigateToView('admin-incidents')}
                  className="ml-2 text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] cursor-pointer"
                >
                  View All Exceptions →
                </button>
              </div>
            </div>

            {/* Exceptions List */}
            {filteredActions.length > 0 ? (
              <div className="divide-y divide-[#F1F5F9]">
                {filteredActions.map((item) => (
                  <div
                    key={item.id}
                    className="py-3.5 first:pt-0 last:pb-0 flex items-center justify-between gap-4 hover:bg-[#F8FAFC] px-2 rounded-xl transition-colors"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${getDotColor(item.dotColor)}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-extrabold text-[#0F172A] truncate">
                            {item.title}
                          </span>
                          {item.assignedTo && (
                            <span className="px-2 py-0.5 bg-blue-50 text-[#2563EB] text-[10px] font-bold rounded">
                              {item.assignedTo}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[#64748B] font-medium mt-0.5">
                          {item.domain} · <span className="text-[#334155] font-semibold">{item.team}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setActiveException(item)}
                        className="px-3 py-1.5 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#334155] font-bold text-xs rounded-lg transition-colors cursor-pointer"
                      >
                        Inspect
                      </button>
                      <button
                        onClick={() => {
                          resolveActionRequired(item.id);
                          triggerToast(`Resolved: "${item.title}"`);
                        }}
                        className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-xs rounded-lg transition-colors cursor-pointer"
                      >
                        Resolve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center space-y-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
                <p className="text-xs font-bold text-[#0F172A]">No critical exceptions requiring action.</p>
                <p className="text-[11px] text-[#64748B]">All pipelines and support SLAs are operational.</p>
              </div>
            )}
          </div>

          {/* =============================================================== */}
          {/* 5. MAIN CONTENT GRID (Global Usage Trend + Operational Health)  */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column (2/3): Usage Trend & Recent Changes */}
            <div className="lg:col-span-2 space-y-8">
              {/* Chart Container */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-extrabold text-[#0F172A]">
                      Global Usage Trend
                    </h3>
                    <p className="text-xs text-[#64748B]">
                      Daily execution volume and API traffic patterns.
                    </p>
                  </div>

                  {/* Metric Switcher */}
                  <div className="bg-[#F8FAFC] p-1 border border-[#E2E8F0] rounded-xl flex items-center gap-1">
                    {[
                      { id: 'calculations', label: 'Calculations' },
                      { id: 'activeUsers', label: 'Users' },
                      { id: 'apiRequests', label: 'API Requests' },
                      { id: 'latencyMs', label: 'Latency (ms)' }
                    ].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setMetricTab(m.id as any)}
                        className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                          metricTab === m.id
                            ? 'bg-white text-[#0F172A] shadow-xs'
                            : 'text-[#64748B] hover:text-[#0F172A]'
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recharts Area Chart */}
                <div className="h-64 w-full pt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={state.usageTrend}>
                      <defs>
                        <linearGradient id="usageGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#2563EB" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                      <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} tickLine={false} />
                      <YAxis
                        stroke="#94A3B8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(val) =>
                          val >= 1000000
                            ? `${(val / 1000000).toFixed(1)}M`
                            : val >= 1000
                            ? `${(val / 1000).toFixed(0)}K`
                            : `${val}`
                        }
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#0F172A',
                          borderRadius: '12px',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey={metricTab}
                        stroke="#2563EB"
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#usageGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Recent Changes Container */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-4">
                <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-4">
                  <h3 className="text-base font-extrabold text-[#0F172A]">
                    Recent System & Operational Changes
                  </h3>
                  <button
                    onClick={() => onNavigateToView('admin-audit')}
                    className="text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] cursor-pointer"
                  >
                    View Audit Log →
                  </button>
                </div>

                <div className="space-y-3">
                  {state.recentChanges.map((chg) => (
                    <div
                      key={chg.id}
                      className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex items-start justify-between gap-4 hover:border-[#CBD5E1] transition-all"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-blue-100 text-[#2563EB] font-bold text-[10px] rounded uppercase">
                            {chg.type}
                          </span>
                          <span className="text-xs font-extrabold text-[#0F172A]">
                            {chg.title}
                          </span>
                        </div>
                        <p className="text-[11px] text-[#64748B] font-medium">
                          {chg.description}
                        </p>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-[10px] font-mono text-[#94A3B8] block">
                          {chg.timestamp}
                        </span>
                        <span className="text-[10px] font-bold text-[#334155] block mt-0.5">
                          By {chg.actor}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column (1/3): Operational Health & Quick Actions */}
            <div className="space-y-8">
              {/* Operational Health Panel */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-5">
                <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
                  <h3 className="text-base font-extrabold text-[#0F172A]">
                    Operational Health
                  </h3>
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-extrabold rounded-lg">
                    99.9% Uptime
                  </span>
                </div>

                <div className="space-y-3">
                  {state.operationalHealth.map((srv) => (
                    <div
                      key={srv.id}
                      className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex items-center justify-between"
                    >
                      <div>
                        <span className="text-xs font-extrabold text-[#0F172A] block">
                          {srv.name}
                        </span>
                        <span className="text-[11px] text-[#64748B]">
                          {srv.subtext}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-[#0F172A]">
                          {srv.statusText}
                        </span>
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Admin Actions */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-4">
                <h3 className="text-base font-extrabold text-[#0F172A]">
                  SuperAdmin Shortcuts
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onNavigateToView('production-command-center');
                      triggerToast('Navigating to Production Engineering');
                    }}
                    className="w-full text-left p-3 bg-[#F8FAFC] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl text-xs font-bold text-[#0F172A] flex items-center justify-between transition-colors cursor-pointer"
                  >
                    <span>Production Engineering (SRE)</span>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                  </button>
                  <button
                    onClick={() => {
                      onNavigateToView('ciso-security-command-center');
                      triggerToast('Navigating to Security Operations');
                    }}
                    className="w-full text-left p-3 bg-[#F8FAFC] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl text-xs font-bold text-[#0F172A] flex items-center justify-between transition-colors cursor-pointer"
                  >
                    <span>CISO & Security Operations</span>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                  </button>
                  <button
                    onClick={() => {
                      onNavigateToView('admin-feature-flags');
                      triggerToast('Navigating to Feature Flags');
                    }}
                    className="w-full text-left p-3 bg-[#F8FAFC] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl text-xs font-bold text-[#0F172A] flex items-center justify-between transition-colors cursor-pointer"
                  >
                    <span>Manage Feature Flags & Kill Switches</span>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* MODAL: INSPECT EXCEPTION                                       */}
      {/* =============================================================== */}
      {activeException && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-md w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h3 className="text-base font-bold text-[#0F172A]">Exception Inspection</h3>
              </div>
              <button
                onClick={() => setActiveException(null)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <span className="text-xs font-bold text-[#64748B] block">Title</span>
                <p className="text-sm font-extrabold text-[#0F172A]">{activeException.title}</p>
              </div>

              <div>
                <span className="text-xs font-bold text-[#64748B] block">Domain & Team</span>
                <p className="text-xs font-semibold text-[#334155]">
                  {activeException.domain} · {activeException.team}
                </p>
              </div>

              <div>
                <span className="text-xs font-bold text-[#64748B] block">Details</span>
                <p className="text-xs text-[#475569] p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl mt-1">
                  {activeException.details}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  assignActionRequired(activeException.id, 'SuperAdmin');
                  triggerToast(`Assigned "${activeException.title}" to SuperAdmin`);
                  setActiveException(null);
                }}
                className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] font-bold text-xs rounded-xl cursor-pointer"
              >
                Assign to Me
              </button>
              <button
                onClick={() => {
                  resolveActionRequired(activeException.id);
                  triggerToast(`Resolved "${activeException.title}"`);
                  setActiveException(null);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Mark Resolved
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
